<script lang="ts">
  import { onDestroy, onMount, untrack } from 'svelte'
  import { EditorState } from '@codemirror/state'
  import { EditorView, basicSetup } from 'codemirror'
  import { markdown } from '@codemirror/lang-markdown'
  import { languages } from '@codemirror/language-data'
  import DOMPurify from 'dompurify'
  import { marked } from 'marked'
  import { tStore } from '../../i18n'
  import { companyProfileApi } from '../../api/companyProfile'
  import { ApiError, isApiError } from '../../api/errors'
  import { useToast } from '../../composables/useToast'
  import { useAbortable } from '../../composables/useAbortable'

  const toast = useToast()
  const { start } = useAbortable()

  let content = $state('')
  let originalContent = $state('')
  let loading = $state(true)
  let saving = $state(false)
  let loadError = $state<ApiError | null>(null)
  let activeTab = $state<'editor' | 'preview'>('editor')
  let confirmingRefresh = $state(false)

  let isDirty = $derived(content !== originalContent)
  let preview = $derived(DOMPurify.sanitize(marked.parse(content) as string))

  let editorContainer: HTMLDivElement = $state()!
  let editorView: EditorView | undefined
  let pageHeader: HTMLElement = $state()!
  let headerHeight = $state(0)

  $effect(() => {
    if (!pageHeader) return
    const obs = new ResizeObserver((entries) => {
      headerHeight = entries[0]?.contentRect.height ?? headerHeight
    })
    obs.observe(pageHeader)
    return () => obs.disconnect()
  })

  onMount(() => {
    void load()
  })

  onDestroy(() => {
    editorView?.destroy()
  })

  // Initialize CodeMirror once the container enters the DOM (after the first
  // load completes). When the container is removed (loading=true hides the
  // card), destroy the editor so it gets cleanly recreated with fresh content
  // when the container comes back. untrack prevents content changes from
  // re-running this effect and recreating the editor on every keystroke.
  $effect(() => {
    if (editorContainer) {
      if (!editorView) {
        editorView = new EditorView({
          state: EditorState.create({
            doc: untrack(() => content),
            extensions: [
              basicSetup,
              markdown({ codeLanguages: languages }),
              EditorView.updateListener.of((update) => {
                if (update.docChanged) {
                  content = update.state.doc.toString()
                }
              }),
              argusEditorTheme,
            ],
          }),
          parent: editorContainer,
        })
      }
    } else {
      editorView?.destroy()
      editorView = undefined
    }
  })

  // When switching back to the editor tab, ask CodeMirror to re-measure so
  // it renders correctly after having been hidden.
  $effect(() => {
    if (activeTab === 'editor') {
      editorView?.requestMeasure()
    }
  })

  function setEditorContent(text: string) {
    if (!editorView) return
    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: text },
    })
  }

  function requestRefresh() {
    if (isDirty) {
      confirmingRefresh = true
    } else {
      void load()
    }
  }

  function cancelRefresh() {
    confirmingRefresh = false
  }

  async function confirmRefresh() {
    confirmingRefresh = false
    await load()
  }

  async function load() {
    const signal = start()
    loading = true
    loadError = null
    try {
      const result = await companyProfileApi.get({ signal })
      content = result.content
      originalContent = result.content
      setEditorContent(result.content)
    } catch (err) {
      if (signal.aborted) return
      loadError = toApiError(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  async function save() {
    saving = true
    try {
      const result = await companyProfileApi.update({ content })
      content = result.content
      originalContent = result.content
      setEditorContent(result.content)
      toast.show($tStore('views.companyProfile.saveSuccess'), 'success')
    } catch (err) {
      toast.show(toApiError(err).message, 'error')
    } finally {
      saving = false
    }
  }

  function discard() {
    content = originalContent
    setEditorContent(originalContent)
  }

  function toApiError(err: unknown): ApiError {
    if (isApiError(err)) return err
    return new ApiError({
      status: 0,
      code: 'UNKNOWN',
      message: err instanceof Error ? err.message : String(err),
    })
  }

  const argusEditorTheme = EditorView.theme({
    '&': {
      fontSize: '0.875rem',
      lineHeight: '1.6',
      height: '100%',
    },
    '.cm-scroller': {
      fontFamily: 'var(--font-mono, ui-monospace, "Cascadia Code", monospace)',
      minHeight: '560px',
    },
    '.cm-content': {
      padding: '1rem 0.5rem',
      caretColor: '#4f46e5',
    },
    '.cm-gutters': {
      backgroundColor: '#f8f8f8',
      borderRight: '1px solid #e2e8f0',
      color: '#94a3b8',
      paddingRight: '4px',
    },
    '.cm-activeLineGutter': { backgroundColor: '#f1f5f9' },
    '.cm-activeLine': { backgroundColor: '#f8faff' },
    '.cm-selectionBackground, ::selection': { backgroundColor: '#c7d2fe' },
    '.cm-focused .cm-selectionBackground': { backgroundColor: '#c7d2fe' },
    '.cm-searchMatch': { backgroundColor: '#fef08a', outline: '1px solid #facc15' },
    '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: '#fde047' },
  })
</script>

<section class="company-profile" style="--cp-header-h: {headerHeight}px">
  <header class="company-profile__header" bind:this={pageHeader}>
    <div>
      <h2>{$tStore('views.companyProfile.title')}</h2>
      <p class="company-profile__subtitle">{$tStore('views.companyProfile.subtitle')}</p>
    </div>
    <div class="company-profile__actions">
      {#if confirmingRefresh}
        <span class="refresh-confirm__label">
          {$tStore('views.companyProfile.refreshConfirm')}
        </span>
        <button class="btn btn--danger" onclick={confirmRefresh}>
          {$tStore('views.companyProfile.refreshConfirmYes')}
        </button>
        <button class="btn btn--secondary" onclick={cancelRefresh}>
          {$tStore('views.companyProfile.refreshConfirmNo')}
        </button>
      {:else}
        <button class="btn btn--secondary" onclick={requestRefresh} disabled={loading || saving}>
          {$tStore('views.companyProfile.refresh')}
        </button>
        {#if isDirty}
          <button class="btn btn--secondary" onclick={discard} disabled={loading || saving}>
            {$tStore('views.companyProfile.discard')}
          </button>
          <button class="btn btn--primary" onclick={save} disabled={loading || saving}>
            {saving ? $tStore('views.companyProfile.saving') : $tStore('views.companyProfile.save')}
          </button>
        {/if}
      {/if}
    </div>
  </header>

  {#if loading}
    <p class="muted">{$tStore('views.companyProfile.loading')}</p>
  {:else if loadError}
    <p class="error">{loadError.message}</p>
  {:else}
    <div class="card">
      <div class="card__toolbar">
        <div class="tabs">
          <button
            class="tab"
            class:tab--active={activeTab === 'editor'}
            onclick={() => (activeTab = 'editor')}
          >
            {$tStore('views.companyProfile.tabEditor')}
          </button>
          <button
            class="tab"
            class:tab--active={activeTab === 'preview'}
            onclick={() => (activeTab = 'preview')}
          >
            {$tStore('views.companyProfile.tabPreview')}
          </button>
        </div>
        {#if isDirty}
          <span class="unsaved-badge">{$tStore('views.companyProfile.unsaved')}</span>
        {/if}
      </div>

      <!-- Editor stays in the DOM so CodeMirror keeps its state; hidden via CSS -->
      <div class="editor-wrap" style:display={activeTab !== 'editor' ? 'none' : ''}>
        <div bind:this={editorContainer} class="codemirror-host"></div>
      </div>

      {#if activeTab === 'preview'}
        <div class="preview">
          {#if content.trim()}
            <!-- eslint-disable-next-line svelte/no-at-html-tags -- `preview` is sanitized with DOMPurify. -->
            {@html preview}
          {:else}
            <p class="preview__empty">{$tStore('views.companyProfile.previewEmpty')}</p>
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .company-profile {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .company-profile__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: v.$space-md;
    flex-wrap: wrap;
    position: sticky;
    top: v.$header-height;
    z-index: 11;
    background: v.$color-surface-elevated;
    padding-bottom: v.$space-sm;

    h2 {
      margin: 0 0 v.$space-xs;
    }
  }

  .company-profile__subtitle {
    margin: 0;
    color: v.$color-text-muted;
    font-size: 0.875rem;
  }

  .company-profile__actions {
    display: flex;
    gap: v.$space-sm;
    align-items: center;
    flex-shrink: 0;
  }

  // ── Card ──────────────────────────────────────────────────────────────────

  .card {
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-md;
    overflow: clip; // clip preserves border-radius without breaking position:sticky inside
  }

  .card__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 v.$space-md;
    border-bottom: 1px solid v.$color-border;
    background: v.$color-surface;
    min-height: 42px;
    position: sticky;
    top: calc(v.$header-height + var(--cp-header-h, 0px));
    z-index: 10;
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────

  .tabs {
    display: flex;
    gap: 0;
  }

  .tab {
    padding: 0.625rem v.$space-md;
    font-size: 0.875rem;
    font-family: inherit;
    font-weight: 500;
    color: v.$color-text-muted;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition:
      color 120ms ease,
      border-color 120ms ease;
    margin-bottom: -1px;

    &:hover {
      color: v.$color-text;
    }

    &--active {
      color: v.$color-primary;
      border-bottom-color: v.$color-primary;
    }
  }

  .unsaved-badge {
    font-size: 0.8125rem;
    color: v.$color-warning-fg;
    font-weight: 500;
  }

  // ── CodeMirror host ───────────────────────────────────────────────────────

  .editor-wrap {
    // Let CodeMirror fill the container; it manages its own scrolling.
    overflow: hidden;
  }

  .codemirror-host {
    // CodeMirror appends its own element here.
    :global(.cm-editor) {
      width: 100%;
      outline: none;
    }

    :global(.cm-editor.cm-focused) {
      outline: none;
    }
  }

  // ── Preview ───────────────────────────────────────────────────────────────

  .preview {
    padding: v.$space-lg;
    min-height: 560px;
    font-size: 0.9rem;
    line-height: 1.7;
    overflow-x: auto;
    color: v.$color-text;

    // Rendered markdown styles
    :global(h1),
    :global(h2),
    :global(h3),
    :global(h4) {
      margin: 1.5em 0 0.5em;
      font-weight: 600;
      line-height: 1.3;
      color: v.$color-text;

      &:first-child {
        margin-top: 0;
      }
    }

    :global(h1) {
      font-size: 1.5rem;
    }
    :global(h2) {
      font-size: 1.25rem;
      border-bottom: 1px solid v.$color-border;
      padding-bottom: 0.3em;
    }
    :global(h3) {
      font-size: 1.1rem;
    }

    :global(p) {
      margin: 0.75em 0;
    }

    :global(ul),
    :global(ol) {
      margin: 0.75em 0;
      padding-left: 1.5em;
    }

    :global(li) {
      margin: 0.25em 0;
    }

    :global(code) {
      font-family: v.$font-mono;
      font-size: 0.85em;
      background: v.$color-surface;
      border: 1px solid v.$color-border;
      border-radius: 3px;
      padding: 0.15em 0.4em;
    }

    :global(pre) {
      background: v.$color-surface;
      border: 1px solid v.$color-border;
      border-radius: v.$radius-sm;
      padding: v.$space-md;
      overflow-x: auto;
      margin: 1em 0;

      :global(code) {
        background: none;
        border: none;
        padding: 0;
        font-size: 0.875rem;
      }
    }

    :global(blockquote) {
      border-left: 3px solid v.$color-border;
      margin: 1em 0;
      padding: 0.25em 1em;
      color: v.$color-text-muted;
    }

    :global(table) {
      border-collapse: collapse;
      width: 100%;
      margin: 1em 0;
      overflow-x: auto;
      display: block;
    }

    :global(th),
    :global(td) {
      border: 1px solid v.$color-border;
      padding: 0.5em 0.75em;
      text-align: left;
    }

    :global(th) {
      background: v.$color-surface;
      font-weight: 600;
    }

    :global(tr:nth-child(even)) {
      background: v.$color-surface;
    }

    :global(hr) {
      border: none;
      border-top: 1px solid v.$color-border;
      margin: 1.5em 0;
    }

    :global(a) {
      color: v.$color-primary;
      text-decoration: underline;
    }
  }

  .preview__empty {
    color: v.$color-text-muted;
    font-style: italic;
  }

  // ── Buttons ───────────────────────────────────────────────────────────────

  .btn {
    padding: v.$space-sm v.$space-md;
    border-radius: v.$radius-sm;
    font-family: inherit;
    font-size: 0.9375rem;
    border: 1px solid transparent;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    &--primary {
      background: v.$color-primary;
      color: v.$color-on-primary;

      &:hover:not(:disabled) {
        background: v.$color-primary-hover;
      }
    }

    &--secondary {
      background: v.$color-surface-elevated;
      color: v.$color-text;
      border-color: v.$color-border;

      &:hover:not(:disabled) {
        background: v.$color-surface;
      }
    }

    &--danger {
      background: v.$color-danger-bg;
      color: v.$color-danger-fg;
      border-color: v.$color-danger-fg;

      &:hover:not(:disabled) {
        opacity: 0.85;
      }
    }
  }

  .refresh-confirm__label {
    font-size: 0.875rem;
    color: v.$color-warning-fg;
    align-self: center;
  }

  // ── States ────────────────────────────────────────────────────────────────

  .muted {
    color: v.$color-text-muted;
  }

  .error {
    color: v.$color-danger-fg;
    background: v.$color-danger-bg;
    padding: v.$space-sm v.$space-md;
    border-radius: v.$radius-sm;
  }
</style>
